// THE PHONE VIEW STAGE (Session BDZ). A full-window layer holding a toolbar and, centred under it, the app in a phone
// shaped frame: a same-origin iframe, so the app inside sees a true phone width and its own media queries fire.
//
// The frame has no laptop chrome around it beyond a thin outline, so an ordinary screenshot of it pastes into a chat
// looking like a phone screen. "Copy path and width" puts the words to paste beside it on the clipboard.
//
// The page inside the frame is read only (api.ts refuses its writes), and its offline stores are a database of their
// own: see phonePreview.ts for the rules and phonePreviewFrame.ts for the frame's half. This file draws and relays.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Copy, Maximize2, RotateCw, X } from "lucide-react";
import { Button } from "../Button";
import { ThemeToggle } from "../ThemeToggle";
import { copyText } from "../../lib/clipboard";
import { useAuth } from "../../lib/auth";
import { useTheme } from "../../lib/theme";
import {
  ENGINEER_ROUTES,
  PHONE_PRESETS,
  PHONE_TEXT_SCALES,
  PHONE_VIEW_FRAME_NAME,
  PHONE_VIEW_MSG,
  copySummary,
  engineerRoutesAvailable,
  fitScale,
  frameSize,
  isInPhonePreviewFrame,
  loadPhoneViewSettings,
  normalisePhonePath,
  phoneViewAvailable,
  previewWritesAllowed,
  savePhoneViewSettings,
  type PhoneTextScale,
  type PhoneViewSettings,
} from "../../lib/phonePreview";
import { closePhoneView, notePhoneViewPath, usePhoneViewState } from "../../lib/phoneViewStore";
import type { Role } from "../../lib/api";
import { useViewportWidth } from "./PhoneViewButton";

const SELECT =
  "rounded-ele border border-ele-field-border bg-ele-field px-2 py-1.5 text-sm text-ele-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ele-green";
const NOWRAP = "whitespace-nowrap";

export const ENGINEER_PREVIEW_NOTICE = "Engineer app preview needs a seeded test engineer. Use a real phone or ask for a demo login.";

/** The stage mounted beside a layout: it shows itself when the store says open and the account may use it. */
export function PhoneViewHost() {
  const { user } = useAuth();
  const { open, path } = usePhoneViewState();
  const viewportWidth = useViewportWidth();
  const allowed = phoneViewAvailable({ role: user?.role, viewportWidth, inFrame: isInPhonePreviewFrame() });
  // The stage shows only while the choice is ON and this account and window qualify. A window narrowed under 900px, a sign-in
  // still in flight or an account that does not qualify hides it without turning the choice off (the remembered state is the
  // person's own, and only the toggle or Close changes it), so a refresh never forgets it while the session is still loading.
  if (!open || !allowed || !user) return null;
  return <PhoneViewStage initialPath={path} role={user.role} onClose={closePhoneView} />;
}

export function PhoneViewStage({ initialPath, role, onClose }: { initialPath: string; role: Role; onClose: () => void }) {
  const origin = window.location.origin;
  const { theme } = useTheme();
  const [settings, setSettings] = useState<PhoneViewSettings>(loadPhoneViewSettings);
  const [landscape, setLandscape] = useState(false);
  const [fit, setFit] = useState(true);
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [draft, setDraft] = useState(initialPath);
  const [pathError, setPathError] = useState("");
  const [copied, setCopied] = useState<"" | "yes" | "no">("");
  const [area, setArea] = useState({ width: 0, height: 0 });
  // The frame is opened once, at the page the toggle was pressed on. Every later move is a message to the app inside,
  // which navigates in place (no reload, no second sign-in).
  const [src] = useState(initialPath);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  // The stage sits BELOW the app's own header, so the header's Phone view toggle stays on screen and clickable while it is on.
  // Measured from the header the layouts mark (data-app-header); with none (a page outside the two shells) it fills the window.
  const [bounds, setBounds] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    const header = document.querySelector("[data-app-header]");
    if (!header) return;
    const measure = () => {
      const r = header.getBoundingClientRect();
      setBounds((prev) => (prev && prev.top === r.bottom && prev.left === r.left ? prev : { top: r.bottom, left: r.left }));
    };
    measure();
    window.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(header);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, []);
  const engineerOk = engineerRoutesAvailable(role);
  const readOnly = !previewWritesAllowed({ dev: import.meta.env.DEV === true, hostname: window.location.hostname });

  const size = frameSize(settings.width, landscape);
  const scale = fit ? fitScale(area, size) : 1;

  const update = (patch: Partial<PhoneViewSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      savePhoneViewSettings(next);
      return next;
    });
  };

  const sendToFrame = (message: Record<string, unknown>) => {
    frameRef.current?.contentWindow?.postMessage(message, origin);
  };
  const sendScale = () => sendToFrame({ type: PHONE_VIEW_MSG.textScale, percent: settings.textScale });
  // The latest sendScale for the listeners below, so they never act on a stale text size.
  const sendScaleRef = useRef(sendScale);
  sendScaleRef.current = sendScale;
  // The path a navigation was asked to reach and the frame has not yet reported, so a request lost to a frame that was not
  // listening yet (it was still loading) is re-sent when the frame says it is ready, and a frame that never gets there is
  // opened afresh. The frame's OWN first report (its starting page) does not count as an answer to a request.
  const pendingPath = useRef<string | null>(null);
  const fallbackTimer = useRef<number | undefined>(undefined);
  const copiedTimer = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      window.clearTimeout(fallbackTimer.current);
      window.clearTimeout(copiedTimer.current);
    },
    [],
  );
  // Put the page that was open when the stage opened back in focus when it closes.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    return () => before?.focus?.();
  }, []);

  useEffect(() => {
    sendScale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.textScale]);

  // What the app inside tells the stage: where it is, and that it is ready. Only the frame we made, on this origin.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { type?: unknown; path?: unknown } | null;
      if (!data || typeof data !== "object") return;
      if (data.type === PHONE_VIEW_MSG.path && typeof data.path === "string") {
        const path = normalisePhonePath(data.path, origin) ?? data.path;
        if (pendingPath.current === path) pendingPath.current = null;
        notePhoneViewPath(path);
        setCurrentPath(path);
        setDraft(path);
        setPathError("");
      } else if (data.type === PHONE_VIEW_MSG.ready) {
        sendScaleRef.current();
        if (pendingPath.current) frameRef.current?.contentWindow?.postMessage({ type: PHONE_VIEW_MSG.navigate, path: pendingPath.current }, origin);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [origin]);

  // Room for the frame: what the area gives it, less its padding.
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const measure = () => setArea({ width: Math.max(0, el.clientWidth - 32), height: Math.max(0, el.clientHeight - 32) });
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Escape inside a field or an open list belongs to that control, not to the stage.
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const goTo = (raw: string) => {
    const path = normalisePhonePath(raw, origin);
    if (!path) {
      setPathError("Enter a path inside /office or /engineer.");
      return;
    }
    setPathError("");
    pendingPath.current = path;
    sendToFrame({ type: PHONE_VIEW_MSG.navigate, path });
    // A frame that failed to load (offline, or a session that ended and landed on a page the app cannot be framed on) never
    // answers: open the path as a fresh page instead, so the controls are never left dead.
    window.clearTimeout(fallbackTimer.current);
    fallbackTimer.current = window.setTimeout(() => {
      if (pendingPath.current === path) {
        pendingPath.current = null;
        frameRef.current?.setAttribute("src", path);
      }
    }, 1500);
  };

  const copy = async () => {
    const ok = await copyText(copySummary({ path: currentPath, width: size.width, textScale: settings.textScale, theme, landscape }));
    setCopied(ok ? "yes" : "no");
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(""), 2500);
  };

  const routeValue = useMemo(() => {
    const plain = currentPath.split("?")[0];
    return [...ENGINEER_ROUTES.map((r) => r.path), "/office"].includes(plain) ? plain : "";
  }, [currentPath]);

  return (
    <div
      role="dialog"
      aria-label="Phone view"
      data-testid="phone-view-stage"
      style={bounds ? { top: bounds.top, left: bounds.left } : undefined}
      className={`fixed bottom-0 right-0 z-50 flex flex-col overscroll-contain bg-ele-base text-ele-body ${bounds ? "" : "inset-0"}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-ele-hairline bg-[var(--ele-shell)] px-3 py-2">
        <Button variant="ghost" size="sm" onClick={onClose} className={NOWRAP} icon={<X aria-hidden size={16} />} aria-label="Close Phone view" autoFocus>
          Close
        </Button>

        <label className="flex items-center gap-1.5 text-sm">
          <span className="text-ele-muted">Screen</span>
          <select
            aria-label="Choose a screen"
            className={SELECT}
            value={routeValue}
            onChange={(e) => e.target.value && goTo(e.target.value)}
          >
            <option value="">Choose a screen</option>
            {engineerOk && (
              <optgroup label="Engineer app">
                {ENGINEER_ROUTES.map((r) => (
                  <option key={r.path} value={r.path}>
                    {r.label}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Office app">
              <option value="/office">Office home</option>
            </optgroup>
          </select>
        </label>

        <form
          className="flex items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            goTo(draft);
          }}
        >
          <input
            aria-label="App path"
            aria-invalid={pathError ? true : undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            className={`${SELECT} w-44 xl:w-64`}
            placeholder="/engineer/diary"
          />
          <Button type="submit" variant="secondary" size="sm">
            Go
          </Button>
        </form>

        <label className="flex items-center gap-1.5 text-sm">
          <span className="text-ele-muted">Width</span>
          <select aria-label="Phone width" className={SELECT} value={settings.width} onChange={(e) => update({ width: Number(e.target.value) })}>
            {PHONE_PRESETS.map((p) => (
              <option key={p.width} value={p.width}>
                {p.width}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-1.5 text-sm">
          <span className="text-ele-muted">Text</span>
          <select
            aria-label="Text size"
            title="Scales text set in rem. Text set in fixed pixels does not change."
            className={SELECT}
            value={settings.textScale}
            onChange={(e) => update({ textScale: Number(e.target.value) as PhoneTextScale })}
          >
            {PHONE_TEXT_SCALES.map((s) => (
              <option key={s} value={s}>
                {s}%
              </option>
            ))}
          </select>
        </label>

        <ThemeToggle />

        <Button
          variant="ghost"
          size="sm"
          className={NOWRAP}
          icon={<RotateCw aria-hidden size={16} />}
          onClick={() => setLandscape((v) => !v)}
          aria-pressed={landscape}
          aria-label="Rotate"
          title={landscape ? "Back to portrait" : "Rotate to landscape"}
        >
          Rotate
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className={NOWRAP}
          icon={<Maximize2 aria-hidden size={16} />}
          onClick={() => setFit((v) => !v)}
          aria-pressed={!fit}
          title={fit ? "Show at actual size" : "Fit to the window"}
        >
          {fit ? "Fit" : "Actual size"}
        </Button>

        <Button variant="secondary" size="sm" className={NOWRAP} icon={<Copy aria-hidden size={16} />} onClick={copy}>
          Copy path and width
        </Button>
        <span role="status" aria-live="polite" className="text-xs text-ele-muted">
          {copied === "yes" ? "Copied" : copied === "no" ? "Could not copy" : ""}
        </span>

        <span data-testid="phone-view-status" className="ml-auto min-w-0 truncate text-xs text-ele-muted">
          {currentPath} · {size.width}px{landscape ? " (landscape)" : ""}
          {readOnly ? " · Read only" : ""}
        </span>
      </div>

      {(!engineerOk || pathError) && (
        <div className="border-b border-ele-hairline px-3 py-1.5 text-sm">
          {!engineerOk && <p role="note">{ENGINEER_PREVIEW_NOTICE}</p>}
          {pathError && <p role="alert">{pathError}</p>}
        </div>
      )}

      <div ref={areaRef} data-hscroll="" className="flex min-h-0 flex-1 items-start overflow-auto p-4 [justify-content:safe_center]">
        {/* The reserved box is the scaled size, so the layout scrolls and centres on what is drawn. */}
        <div style={{ width: size.width * scale, height: size.height * scale }} className="flex-none">
          <div
            data-testid="phone-view-frame"
            style={{ width: size.width, height: size.height, transform: `scale(${scale})`, transformOrigin: "top left" }}
            className="overflow-hidden rounded-[28px] border border-ele-hairline-strong bg-ele-base"
          >
            <iframe
              ref={frameRef}
              name={PHONE_VIEW_FRAME_NAME}
              title="Phone view"
              src={src}
              onLoad={sendScale}
              style={{ width: size.width, height: size.height }}
              className="block border-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
